import styled, { keyframes } from 'styled-components';

const backdropIn = keyframes`
	from {
		opacity: 0;
	}

	to {
		opacity: 1;
	}
`;

const shellIn = keyframes`
	from {
		opacity: 0;
		transform: scale(0.9) translateY(18px);
	}

	to {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
`;

const glowPulse = keyframes`
	0%,
	100% {
		opacity: 0.55;
		transform: scale(1);
	}

	50% {
		opacity: 0.85;
		transform: scale(1.04);
	}
`;

export const imageIn = keyframes`
	from {
		opacity: 0;
		transform: scale(1.02);
	}

	to {
		opacity: 1;
		transform: scale(1);
	}
`;

export const ModalBackdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: 6000;
	display: grid;
	place-items: center;
	padding: clamp(12px, 3vw, 28px);
	background:
		radial-gradient(circle at 18% 12%, rgba(176, 38, 255, 0.22), transparent 34%),
		radial-gradient(circle at 82% 88%, rgba(255, 77, 242, 0.16), transparent 38%),
		rgba(3, 1, 6, 0.72);
	backdrop-filter: blur(28px) saturate(1.12);
	animation: ${backdropIn} 260ms ease-out;

	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		filter: blur(60px);
		pointer-events: none;
		animation: ${glowPulse} 4.8s ease-in-out infinite;
	}

	&::before {
		width: min(42vw, 280px);
		height: min(42vw, 280px);
		top: 8%;
		left: 10%;
		background: rgba(176, 38, 255, 0.28);
	}

	&::after {
		width: min(36vw, 240px);
		height: min(36vw, 240px);
		right: 8%;
		bottom: 10%;
		background: rgba(255, 77, 242, 0.22);
		animation-delay: 1.2s;
	}
`;

export const ModalShell = styled.div`
	position: relative;
	width: min(420px, calc(100vw - 24px));
	max-height: min(86vh, 760px);
	display: grid;
	grid-template-rows: auto minmax(0, 1fr) auto;
	overflow: hidden;
	border-radius: 28px;
	border: 1px solid rgba(255, 180, 236, 0.24);
	background:
		radial-gradient(circle at 88% 0%, rgba(255, 211, 107, 0.14), transparent 34%),
		radial-gradient(circle at 8% 100%, rgba(255, 77, 242, 0.16), transparent 38%),
		linear-gradient(145deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.02)),
		rgba(12, 5, 18, 0.92);
	box-shadow:
		0 34px 96px rgba(0, 0, 0, 0.62),
		0 0 46px rgba(255, 77, 242, 0.16),
		inset 0 1px 0 rgba(255, 255, 255, 0.08);
	backdrop-filter: blur(28px) saturate(1.16);
	animation: ${shellIn} 360ms cubic-bezier(0.22, 1, 0.36, 1);
`;

export const ModalHeader = styled.div`
	position: relative;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 16px 16px 12px;
	border-bottom: 1px solid rgba(255, 214, 255, 0.1);
`;

export const ModalCounter = styled.span`
	flex: 1;
	text-align: center;
	font-size: 0.78rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: rgba(216, 189, 228, 0.82);
`;

export const IconButton = styled.button`
	flex: 0 0 auto;
	width: 40px;
	height: 40px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 214, 255, 0.16);
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.05);
	color: rgba(255, 247, 255, 0.92);
	cursor: pointer;
	transition:
		transform 180ms ease,
		background 180ms ease,
		border-color 180ms ease,
		opacity 180ms ease;

	svg {
		width: 18px;
		height: 18px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:hover:not(:disabled),
	&:focus-visible {
		transform: scale(1.05);
		background: rgba(255, 77, 242, 0.16);
		border-color: rgba(255, 77, 242, 0.34);
	}

	&:disabled {
		opacity: 0;
		pointer-events: none;
	}
`;

export const ImageStage = styled.div`
	position: relative;
	min-height: min(68vh, 560px);
	background: #050208;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 1;
		pointer-events: none;
		background: linear-gradient(
			180deg,
			rgba(255, 255, 255, 0.04) 0%,
			transparent 16%,
			transparent 78%,
			rgba(8, 3, 12, 0.34) 100%
		);
	}
`;

export const ImageSkeleton = styled.div`
	position: absolute;
	inset: 0;
	z-index: 0;
`;

export const ModalImage = styled.img`
	position: absolute;
	inset: 0;
	z-index: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	object-position: center center;
	opacity: ${({ $visible }) => ($visible ? 1 : 0)};
	animation: ${({ $visible }) => ($visible ? imageIn : 'none')} 420ms cubic-bezier(0.22, 1, 0.36, 1);
`;

const StageNavButton = styled(IconButton)`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 3;
	background: rgba(8, 3, 12, 0.58);
	backdrop-filter: blur(12px);

	&:hover:not(:disabled),
	&:focus-visible {
		transform: translateY(-50%) scale(1.05);
	}
`;

export const PrevButton = styled(StageNavButton)`
	left: 14px;
`;

export const NextButton = styled(StageNavButton)`
	right: 14px;
`;

export const ModalFooter = styled.div`
	position: relative;
	z-index: 2;
	padding: 14px 18px 18px;
	border-top: 1px solid rgba(255, 214, 255, 0.1);
`;

export const Caption = styled.p`
	margin: 0;
	padding: 12px 14px;
	border-radius: 14px;
	border: 1px solid rgba(255, 214, 255, 0.1);
	background: rgba(255, 255, 255, 0.04);
	color: rgba(255, 247, 255, 0.9);
	font-size: 0.9rem;
	line-height: 1.45;
	text-align: center;
`;
